import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const GESTURE = {
  TAP_MOVE_PX: 22,
  TAP_MAX_MS: 500,
  DRAG_START_PX: 15,
  CLICK_SUPPRESS_MS: 180,
};

const ZOOM_LEVELS = ['is-zoomed', 'is-zoomed-2'];

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'gallery', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #state = {
    mode: 'idle',
    dragMoved: false,
    suppressClickUntil: 0,
    activePointerId: null,
    activeImage: null,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startPanX: 0,
    startPanY: 0,
    panX: 0,
    panY: 0,
    panRafId: 0,
    pendingPanX: 0,
    pendingPanY: 0,
    pendingPanImage: null,
    touch: null,
    zoomLockedScrollLeft: 0,
    rtlType: null,
    lastSelectedIndex: 0,
  };

  #handlers = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initZoomInteractions();
    this.#syncSelectedFromScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupZoomInteractions();
  }

  async open(index, event) {
    event.preventDefault();
    const { dialog, media, thumbnails } = this.refs;
    const safeIndex = this.#clampIndex(index);
    const targetImage = media[safeIndex];
    const targetThumbnail = thumbnails.children[safeIndex];

    const openDialog = () => {
      dialog.showModal();
      targetThumbnail?.scrollIntoView({ behavior: 'instant' });
      targetImage?.scrollIntoView({ behavior: 'instant' });
      this.#clearInteractiveFocus(event);
    };

    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    if (!this.#canUseViewTransition() || !sourceImage || !targetImage) {
      openDialog();
      this.selectThumbnail(safeIndex, { behavior: 'instant' });
      return;
    }

    await this.#runTransition(sourceImage, targetImage, openDialog);
    this.selectThumbnail(safeIndex, { behavior: 'instant' });
  }

  #canUseViewTransition() {
    if (!supportsViewTransitions() || prefersReducedMotion()) return false;
    return !window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
  }

  #clearInteractiveFocus(event) {
    const target = event?.target;
    if (target instanceof HTMLElement) target.blur();
    const opener = target instanceof Element ? target.closest('slideshow-slide, .product-media-container, button, a') : null;
    if (opener instanceof HTMLElement) opener.blur();
    const active = document.activeElement;
    if (active instanceof HTMLElement && !this.contains(active) && active !== document.body) {
      active.blur();
    }
  }

  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;
    if (image.currentSrc === highResolutionUrl || image.src === highResolutionUrl) {
      this.#highResImagesLoaded.add(highResolutionUrl);
      return false;
    }
    preloadImage(highResolutionUrl);
    const probe = new Image();
    probe.decoding = 'async';
    probe.onload = () => {
      if (!image.isConnected) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    probe.src = highResolutionUrl;
    return true;
  }

  async close() {
    const { dialog, media } = this.refs;
    const activeIndex = this.#getActiveIndex();
    const activeMedia = media[activeIndex];
    if (!this.#canUseViewTransition() || !activeMedia) return this.closeDialog();

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );
    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];
    if (!slide || !(slide instanceof HTMLElement)) return this.closeDialog();
    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);
    await this.#runTransition(activeMedia, slide, () => this.closeDialog());
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#resetZoomState();
    const active = document.activeElement;
    if (active instanceof HTMLElement && dialog.contains(active)) active.blur();
    dialog.close();
    const restored = document.activeElement;
    if (restored instanceof HTMLElement && restored.closest('media-gallery, slideshow-slide, .product-media-container')) {
      restored.blur();
    }
    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#clampIndex(index), { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const safeIndex = this.#clampIndex(index);
    const { media } = this.refs;
    if (!media[safeIndex]) return;
    this.#loadHighResolutionImage(media[safeIndex]);
  }

  async handlePrevClick() {
    const nextIndex = this.#clampIndex(this.#getActiveIndex() - 1);
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(nextIndex, { behavior });
  }

  async handleNextClick() {
    const nextIndex = this.#clampIndex(this.#getActiveIndex() + 1);
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(nextIndex, { behavior });
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    const safeIndex = this.#clampIndex(index);
    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[safeIndex];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
    if (this.#state.mode === 'zoomed' || this.#state.mode === 'panning') {
      this.#resetZoomState();
    }
    this.#setSelectedThumbnail(safeIndex);
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });
    const targetImage = media[safeIndex];
    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });
      this.#loadHighResolutionImage(targetImage);
    }
    this.#state.lastSelectedIndex = safeIndex;
    this.dispatchEvent(new ZoomMediaSelectedEvent(safeIndex));
  }

  #initZoomInteractions() {
    const gallery = this.refs.gallery;
    const dialog = this.refs.dialog;
    if (!gallery || this.#handlers) return;
    this.#state.rtlType = this.#detectRtlScrollType();
    this.#handlers = {
      scroll: () => this.#onScroll(),
      pointerDown: (event) => this.#onPointerDown(event),
      pointerMove: (event) => this.#onPointerMove(event),
      pointerUp: (event) => this.#onPointerUp(event),
      pointerCancel: (event) => this.#onPointerUp(event),
      clickCapture: (event) => this.#onClickCapture(event),
      dialogClick: (event) => this.#onDialogClick(event),
      touchStart: (event) => this.#onTouchStart(event),
      touchMove: (event) => this.#onTouchMove(event),
      touchEnd: (event) => this.#onTouchEnd(event),
      touchCancel: () => this.#onTouchCancel(),
      wheel: (event) => this.#onWheel(event),
    };
    gallery.addEventListener('scroll', this.#handlers.scroll, { passive: true });
    gallery.addEventListener('pointerdown', this.#handlers.pointerDown);
    gallery.addEventListener('pointermove', this.#handlers.pointerMove);
    gallery.addEventListener('pointerup', this.#handlers.pointerUp);
    gallery.addEventListener('pointercancel', this.#handlers.pointerCancel);
    gallery.addEventListener('click', this.#handlers.clickCapture, true);
    gallery.addEventListener('touchstart', this.#handlers.touchStart, { passive: false });
    gallery.addEventListener('touchmove', this.#handlers.touchMove, { passive: false });
    gallery.addEventListener('touchend', this.#handlers.touchEnd, { passive: false, capture: true });
    gallery.addEventListener('touchcancel', this.#handlers.touchCancel, { passive: true, capture: true });
    gallery.addEventListener('wheel', this.#handlers.wheel, { passive: false });
    if (dialog) {
      dialog.addEventListener('click', this.#handlers.dialogClick);
    }
  }

  #cleanupZoomInteractions() {
    const gallery = this.refs.gallery;
    const dialog = this.refs.dialog;
    if (!gallery || !this.#handlers) return;
    gallery.removeEventListener('scroll', this.#handlers.scroll);
    gallery.removeEventListener('pointerdown', this.#handlers.pointerDown);
    gallery.removeEventListener('pointermove', this.#handlers.pointerMove);
    gallery.removeEventListener('pointerup', this.#handlers.pointerUp);
    gallery.removeEventListener('pointercancel', this.#handlers.pointerCancel);
    gallery.removeEventListener('click', this.#handlers.clickCapture, true);
    gallery.removeEventListener('touchstart', this.#handlers.touchStart);
    gallery.removeEventListener('touchmove', this.#handlers.touchMove);
    gallery.removeEventListener('touchend', this.#handlers.touchEnd, true);
    gallery.removeEventListener('touchcancel', this.#handlers.touchCancel, true);
    gallery.removeEventListener('wheel', this.#handlers.wheel);
    if (dialog) {
      dialog.removeEventListener('click', this.#handlers.dialogClick);
    }
    this.#handlers = null;
    this.#resetZoomState();
  }

  #runTransition(sourceElement, targetElement, action) {
    const transitionName = 'gallery-item';
    sourceElement.style.setProperty('view-transition-name', transitionName);
    return startViewTransition(() => {
      sourceElement.style.removeProperty('view-transition-name');
      action();
      targetElement.style.setProperty('view-transition-name', transitionName);
    }).finally(() => {
      sourceElement.style.removeProperty('view-transition-name');
      targetElement.style.removeProperty('view-transition-name');
    });
  }

  #getMediaCount() {
    return this.refs.media?.length || 0;
  }

  #clampIndex(index) {
    const count = this.#getMediaCount();
    if (count <= 0) return 0;
    if (!Number.isFinite(index)) return 0;
    return Math.min(Math.max(Math.trunc(index), 0), count - 1);
  }

  #isImageZoomed(image) {
    if (!image) return false;
    return ZOOM_LEVELS.some((name) => image.classList.contains(name));
  }

  #clearZoomClasses(image) {
    if (!image) return;
    ZOOM_LEVELS.forEach((name) => image.classList.remove(name));
  }

  #resetPan(image) {
    this.#state.panX = 0;
    this.#state.panY = 0;
    if (image) {
      image.style.setProperty('--dialog-image-pan-x', '0px');
      image.style.setProperty('--dialog-image-pan-y', '0px');
    }
  }

  #setZoomActive(isActive) {
    const gallery = this.refs.gallery;
    if (isActive) {
      gallery.classList.add('zoom-active');
      gallery.style.overflowX = 'hidden';
      gallery.style.overflowY = 'hidden';
      this.#state.zoomLockedScrollLeft = this.#getNormalizedScrollLeft();
      this.#state.mode = 'zoomed';
      return;
    }
    gallery.classList.remove('zoom-active');
    gallery.style.overflowX = '';
    gallery.style.overflowY = '';
    this.#state.zoomLockedScrollLeft = 0;
    if (this.#state.mode !== 'dragging') this.#state.mode = 'idle';
  }

  #toggleImageZoom(image) {
    if (!image) return;
    const hasZoom1 = image.classList.contains(ZOOM_LEVELS[0]);
    const hasZoom2 = image.classList.contains(ZOOM_LEVELS[1]);
    if (!hasZoom1 && !hasZoom2) {
      image.classList.add(ZOOM_LEVELS[0]);
      this.#resetPan(image);
      this.#setZoomActive(true);
      return;
    }
    this.#clearZoomClasses(image);
    this.#resetPan(image);
    this.#setZoomActive(false);
  }

  #resetZoomState() {
    const activeImage = this.#resolveImage(this.refs.media?.[this.#getActiveIndex()]);
    this.#clearZoomClasses(activeImage);
    this.#resetPan(activeImage);
    this.#setZoomActive(false);
    this.#state.mode = 'idle';
    this.#state.dragMoved = false;
    this.#state.activePointerId = null;
    this.#state.activeImage = null;
    this.#state.touch = null;
    this.#state.suppressClickUntil = 0;
    if (this.#state.panRafId) {
      cancelAnimationFrame(this.#state.panRafId);
      this.#state.panRafId = 0;
    }
    this.#state.pendingPanImage = null;
  }

  #resolveImage(target) {
    if (!target || !(target instanceof Element)) return null;
    if (target instanceof HTMLImageElement && target.closest('.dialog-zoomed-gallery')) return target;

    const media = target.closest('.product-media');
    if (!media || !media.closest('.dialog-zoomed-gallery')) return null;

    const image = media.querySelector('img.product-media__image, .product-media img');
    return image instanceof HTMLImageElement ? image : null;
  }

  #resolveImageFromEvent(event) {
    if (!event || typeof event.clientX !== 'number' || typeof event.clientY !== 'number') {
      return null;
    }

    let container = null;
    if (event.target instanceof Element) {
      container = event.target.closest('.dialog-zoomed-gallery > .product-media-container');
    }

    const activeIndex = this.#getActiveIndex();
    const activeMedia = this.refs.media?.[activeIndex];
    if (!(activeMedia instanceof HTMLElement)) return null;

    if (container && container !== activeMedia) return null;

    const scope = container || activeMedia;
    const image = scope.querySelector('img.product-media__image, .product-media img');
    if (!(image instanceof HTMLImageElement)) return null;

    const rect = image.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    const { clientX: x, clientY: y } = event;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      return null;
    }

    return image;
  }

  #isInteractiveMediaTarget(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        '.product-media-container--video, .product-media-container--external_video, .product-media-container--model, video, model-viewer, iframe'
      )
    );
  }

  #isLightboxControl(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('[data-lightbox-control]'));
  }

  #isRtl() {
    return (
      document.documentElement.dir === 'rtl' ||
      getComputedStyle(document.documentElement).direction === 'rtl' ||
      getComputedStyle(document.body).direction === 'rtl'
    );
  }

  #detectRtlScrollType() {
    if (!this.#isRtl()) return 'ltr';
    const probe = document.createElement('div');
    const inner = document.createElement('div');
    probe.style.width = '4px';
    probe.style.height = '1px';
    probe.style.overflow = 'scroll';
    probe.style.direction = 'rtl';
    inner.style.width = '8px';
    inner.style.height = '1px';
    probe.appendChild(inner);
    document.body.appendChild(probe);
    probe.scrollLeft = 1;
    const value = probe.scrollLeft;
    document.body.removeChild(probe);
    if (value < 0) return 'negative';
    return 'reverse';
  }

  #getNormalizedScrollLeft() {
    const gallery = this.refs.gallery;
    const max = Math.max(gallery.scrollWidth - gallery.clientWidth, 0);
    const raw = gallery.scrollLeft;
    if (!this.#isRtl()) return Math.min(Math.max(raw, 0), max);
    if (this.#state.rtlType === 'negative') return Math.min(Math.max(-raw, 0), max);
    return Math.min(Math.max(max - raw, 0), max);
  }

  #setNormalizedScrollLeft(value) {
    const gallery = this.refs.gallery;
    const max = Math.max(gallery.scrollWidth - gallery.clientWidth, 0);
    const normalized = Math.min(Math.max(value, 0), max);
    if (!this.#isRtl()) {
      gallery.scrollLeft = normalized;
      return;
    }
    if (this.#state.rtlType === 'negative') {
      gallery.scrollLeft = -normalized;
      return;
    }
    gallery.scrollLeft = max - normalized;
  }

  #getActiveIndex() {
    const count = this.#getMediaCount();
    if (count === 0) return 0;
    const gallery = this.refs.gallery;
    const width = Math.max(gallery.clientWidth, 1);
    const normalized = this.#getNormalizedScrollLeft();
    return this.#clampIndex(Math.round(normalized / width));
  }

  #setSelectedThumbnail(index) {
    const { thumbnails } = this.refs;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });
    this.#state.lastSelectedIndex = index;
  }

  #syncSelectedFromScroll() {
    const index = this.#getActiveIndex();
    if (index === this.#state.lastSelectedIndex) return;
    this.#setSelectedThumbnail(index);
    const media = this.refs.media[index];
    if (media) this.#loadHighResolutionImage(media);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #queuePanUpdate(image, x, y) {
    if (!image) return;
    this.#state.pendingPanX = x;
    this.#state.pendingPanY = y;
    this.#state.pendingPanImage = image;
    if (this.#state.panRafId) return;
    this.#state.panRafId = requestAnimationFrame(() => {
      const target = this.#state.pendingPanImage;
      const panX = this.#state.pendingPanX;
      const panY = this.#state.pendingPanY;
      this.#state.panRafId = 0;
      if (!target) return;
      target.style.setProperty('--dialog-image-pan-x', `${panX}px`);
      target.style.setProperty('--dialog-image-pan-y', `${panY}px`);
    });
  }

  #onScroll() {
    if (this.#state.mode === 'zoomed' || this.#state.mode === 'panning') {
      this.#setNormalizedScrollLeft(this.#state.zoomLockedScrollLeft);
      return;
    }
    this.#syncSelectedFromScroll();
  }

  #onWheel(event) {
    if (this.#state.mode === 'zoomed' || this.#state.mode === 'panning') {
      event.preventDefault();
      event.stopPropagation();
      this.#setNormalizedScrollLeft(this.#state.zoomLockedScrollLeft);
      if (event.deltaY > 0) {
        const activeImage = this.#resolveImage(this.refs.media?.[this.#getActiveIndex()]);
        this.#clearZoomClasses(activeImage);
        this.#resetPan(activeImage);
        this.#setZoomActive(false);
      }
    }
  }

  #onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    const gallery = this.refs.gallery;
    if (this.#isLightboxControl(event.target)) {
      this.#state.mode = 'idle';
      this.#state.dragMoved = false;
      this.#state.activePointerId = null;
      this.#state.activeImage = null;
      return;
    }
    const imageTarget = this.#resolveImageFromEvent(event);
    const activeIndex = this.#getActiveIndex();
    const currentImage = this.#resolveImage(this.refs.media[activeIndex]);
    const dragImage = imageTarget && this.#isImageZoomed(imageTarget)
      ? imageTarget
      : currentImage && this.#isImageZoomed(currentImage)
        ? currentImage
        : null;
    this.#state.dragMoved = false;
    this.#state.activePointerId = event.pointerId;
    if (imageTarget && dragImage) {
      this.#state.activeImage = dragImage;
      this.#state.startPanX = this.#state.panX;
      this.#state.startPanY = this.#state.panY;
      this.#state.startX = event.clientX;
      this.#state.startY = event.clientY;
      this.#state.mode = 'panning';
      gallery.setPointerCapture(event.pointerId);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (imageTarget) return;
    if (currentImage && this.#isImageZoomed(currentImage)) return;
    this.#state.mode = 'dragging';
    this.#state.startX = event.clientX;
    this.#state.startY = event.clientY;
    this.#state.startScrollLeft = this.#getNormalizedScrollLeft();
    gallery.setPointerCapture(event.pointerId);
  }

  #onPointerMove(event) {
    if (this.#state.mode === 'panning' && this.#state.activeImage) {
      const dx = event.clientX - this.#state.startX;
      const dy = event.clientY - this.#state.startY;
      this.#state.panX = this.#state.startPanX + dx;
      this.#state.panY = this.#state.startPanY + dy;
      if (Math.abs(dx) > GESTURE.DRAG_START_PX || Math.abs(dy) > GESTURE.DRAG_START_PX) {
        this.#state.dragMoved = true;
      }
      this.#queuePanUpdate(this.#state.activeImage, this.#state.panX, this.#state.panY);
      return;
    }
    if (this.#state.mode !== 'dragging') return;
    const dx = event.clientX - this.#state.startX;
    if (Math.abs(dx) > GESTURE.DRAG_START_PX) this.#state.dragMoved = true;
    this.#setNormalizedScrollLeft(this.#state.startScrollLeft - dx);
  }

  #onPointerUp(event) {
    const gallery = this.refs.gallery;
    const wasPanning = this.#state.mode === 'panning';
    const wasDragging = this.#state.mode === 'dragging';
    const movedDuringGesture = this.#state.dragMoved;
    if (this.#state.activePointerId !== null && gallery.hasPointerCapture(this.#state.activePointerId)) {
      gallery.releasePointerCapture(this.#state.activePointerId);
    }
    if (this.#state.mode === 'panning') this.#state.mode = 'zoomed';
    if (this.#state.mode === 'dragging') this.#state.mode = 'idle';
    if ((wasPanning || wasDragging) && movedDuringGesture) {
      this.#state.suppressClickUntil = Date.now() + GESTURE.CLICK_SUPPRESS_MS;
      this.#state.dragMoved = false;
    }
    this.#state.activePointerId = null;
    this.#state.activeImage = null;
    if (!event) return;
  }

  #onTouchStart(event) {
    if (this.#isLightboxControl(event.target)) {
      this.#state.touch = null;
      return;
    }
    if (event.touches.length !== 1) {
      this.#state.touch = null;
      return;
    }
    const touch = event.touches[0];
    const image = this.#resolveImageFromEvent({
      clientX: touch.clientX,
      clientY: touch.clientY,
      target: event.target,
    });
    if (!image) {
      this.#state.touch = null;
      return;
    }
    const isZoomed = this.#isImageZoomed(image);
    if (isZoomed) {
      this.#setNormalizedScrollLeft(this.#state.zoomLockedScrollLeft);
      event.preventDefault();
      this.#state.mode = 'panning';
    } else {
      this.#state.mode = 'idle';
    }
    this.#state.touch = {
      id: touch.identifier,
      image,
      x: touch.clientX,
      y: touch.clientY,
      startTs: performance.now(),
      startPanX: this.#state.panX,
      startPanY: this.#state.panY,
      moved: false,
      zoomed: isZoomed,
    };
  }

  #onTouchMove(event) {
    if (!this.#state.touch) return;
    const t = Array.from(event.touches).find((touch) => touch.identifier === this.#state.touch.id);
    if (!t) return;
    const dx = t.clientX - this.#state.touch.x;
    const dy = t.clientY - this.#state.touch.y;
    if (Math.abs(dx) > GESTURE.TAP_MOVE_PX || Math.abs(dy) > GESTURE.TAP_MOVE_PX) {
      this.#state.touch.moved = true;
    }
    if (this.#state.touch.zoomed) {
      event.preventDefault();
      event.stopPropagation();
      this.#setNormalizedScrollLeft(this.#state.zoomLockedScrollLeft);
      this.#state.panX = this.#state.touch.startPanX + dx;
      this.#state.panY = this.#state.touch.startPanY + dy;
      this.#queuePanUpdate(this.#state.touch.image, this.#state.panX, this.#state.panY);
      this.#state.mode = 'panning';
    }
  }

  #onTouchEnd(event) {
    if (!this.#state.touch) return;
    const changed = Array.from(event.changedTouches).find((touch) => touch.identifier === this.#state.touch.id);
    if (!changed) return;
    const touchState = this.#state.touch;
    this.#state.touch = null;
    const isTap = !touchState.moved && performance.now() - touchState.startTs <= GESTURE.TAP_MAX_MS;
    if (touchState.zoomed && !isTap) {
      this.#state.mode = 'zoomed';
      this.#state.suppressClickUntil = Date.now() + GESTURE.CLICK_SUPPRESS_MS;
      return;
    }
    if (!isTap) {
      this.#state.suppressClickUntil = Date.now() + GESTURE.CLICK_SUPPRESS_MS;
      return;
    }
    const dx = Math.abs(changed.clientX - touchState.x);
    const dy = Math.abs(changed.clientY - touchState.y);
    if (dx > GESTURE.TAP_MOVE_PX || dy > GESTURE.TAP_MOVE_PX) return;
    this.#toggleImageZoom(touchState.image);
  }

  #onTouchCancel() {
    this.#state.touch = null;
    if (this.#state.mode === 'panning') this.#state.mode = 'zoomed';
  }

  #shouldSuppressClick() {
    return this.#state.dragMoved || Date.now() < this.#state.suppressClickUntil;
  }

  #closeFromBackdrop(event) {
    if (this.#shouldSuppressClick()) {
      event.preventDefault();
      event.stopPropagation();
      this.#state.dragMoved = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  #onDialogClick(event) {
    if (!this.refs.dialog?.open) return;
    if (this.#isLightboxControl(event.target)) return;
    if (event.target !== this.refs.dialog) return;
    this.#closeFromBackdrop(event);
  }

  #onClickCapture(event) {
    if (this.#isLightboxControl(event.target)) {
      this.#state.dragMoved = false;
      return;
    }

    if (this.#isInteractiveMediaTarget(event.target)) {
      return;
    }

    const image = this.#resolveImageFromEvent(event);
    const activeImage = this.#resolveImage(this.refs.media?.[this.#getActiveIndex()]);
    const isZoomed = Boolean(activeImage && this.#isImageZoomed(activeImage));

    if (this.#shouldSuppressClick()) {
      event.preventDefault();
      event.stopPropagation();
      this.#state.dragMoved = false;
      return;
    }

    if (!image) {
      if (isZoomed) {
        this.#resetZoomState();
      }
      this.#closeFromBackdrop(event);
      return;
    }

    if (isZoomed) {
      this.#toggleImageZoom(image);
      this.#state.suppressClickUntil = 0;
      this.#state.dragMoved = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.#state.dragMoved) {
      event.preventDefault();
      event.stopPropagation();
      this.#state.dragMoved = false;
      return;
    }

    this.#toggleImageZoom(image);
    event.preventDefault();
    event.stopPropagation();
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
